import {
    BdsGrid,
    BdsIcon,
    BdsLoadingSpinner,
    BdsTable,
    BdsTableBody,
    BdsTableCell,
    BdsTableHeader,
    BdsTableRow,
    BdsTableTh,
    BdsTooltip,
    BdsTypo,
} from 'blip-ds/dist/blip-ds-react/components';
import { TableRowEmptyState } from 'components/TableRowEmptyState';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { AgentsTableProps, AggregatedAgent } from 'typings';
import { weightedAverageTime } from 'utils';

export const AgentsTable: React.FC<AgentsTableProps> = ({
    data: agentsData,
    ticketsInQueue,
    isLoading,
    hasMultipleBots,
}: AgentsTableProps) => {
    const { t } = useTranslation();
    const agentsDataWithTicketsInQueue = useMemo(() => {
        const ticketsInQueueByBotId = ticketsInQueue.reduce<Record<string, number>>((acc, ticket) => {
            acc[ticket.botId] = (acc[ticket.botId] ?? 0) + 1;
            return acc;
        }, {});

        const statusPriority: Record<string, number> = { Online: 3, 'Em pausa': 2, Invisível: 1 };
        const agentMap = new Map<string, AggregatedAgent>();

        for (const agentData of agentsData) {
            const queueCount = ticketsInQueueByBotId[agentData.botId] ?? 0;
            const existing = agentMap.get(agentData.name);

            if (existing) {
                agentMap.set(agentData.name, {
                    ...existing,
                    openedTickets: existing.openedTickets + agentData.openedTickets,
                    ticketsInQueue: existing.ticketsInQueue + queueCount,
                    status:
                        (statusPriority[agentData.status] ?? 0) > (statusPriority[existing.status] ?? 0)
                            ? agentData.status
                            : existing.status,
                    averageResponseTime: weightedAverageTime(
                        existing.averageResponseTime,
                        existing.ticketsCount,
                        agentData.averageResponseTime,
                        agentData.ticketsCount,
                    ),
                    averageAttendanceTime: weightedAverageTime(
                        existing.averageAttendanceTime,
                        existing.ticketsCount,
                        agentData.averageAttendanceTime,
                        agentData.ticketsCount,
                    ),
                    ticketsCount: existing.ticketsCount + agentData.ticketsCount,
                });
            } else {
                agentMap.set(agentData.name, { ...agentData, ticketsInQueue: queueCount });
            }
        }

        return Array.from(agentMap.values());
    }, [agentsData, ticketsInQueue]);

    const getStatusLabel = (status: string) => {
        switch (status) {
            case 'Online':
                return t('go-personal-agent-status-online');
            case 'Em pausa':
                return t('go-personal-agent-status-pause');
            case 'Invisível':
                return t('go-personal-agent-status-invisible');
            default:
                return status;
        }
    };

    const getStatusBagde = (status: string) => {
        switch (status) {
            case 'Online':
                return '';
            case 'Em pausa':
                return 'backgroundYellow';
            case 'Invisível':
                return 'backgroundGrey';
            default:
                return 'backgroundGrey';
        }
    };
    return (
        <>
            {isLoading ? (
                <BdsGrid xxs="12" alignItems="center" justifyContent="center" height="250px" padding="t-5">
                    <BdsLoadingSpinner />
                </BdsGrid>
            ) : (
                <div className="data-block-table data-block-table--agents">
                    <BdsTable>
                        <BdsTableHeader>
                            <BdsTableRow>
                                <BdsTableTh justifyContent="left">{t('go-personal-table-column-agent')}</BdsTableTh>
                                <BdsTableTh justifyContent="center">{t('go-personal-table-column-status')}</BdsTableTh>
                                <BdsTableTh justifyContent="center">
                                    <BdsGrid gap="half" justifyContent="center" alignItems="center">
                                        {t('go-personal-table-column-tickets-queue')}
                                        <BdsTooltip
                                            position="right-center"
                                            tooltipText={t('go-personal-agents-table-tooltip-queue')}
                                        >
                                            <BdsIcon name="info" size="x-small" />
                                        </BdsTooltip>
                                    </BdsGrid>
                                </BdsTableTh>
                                <BdsTableTh justifyContent="center">
                                    {t('go-personal-table-column-tickets-attendance')}
                                </BdsTableTh>
                                {!hasMultipleBots && (
                                    <>
                                        <BdsTableTh justifyContent="center">
                                            {t('go-personal-table-column-avg-response')}
                                        </BdsTableTh>
                                        <BdsTableTh justifyContent="center">
                                            {t('go-personal-table-column-avg-time')}
                                        </BdsTableTh>
                                    </>
                                )}
                            </BdsTableRow>
                        </BdsTableHeader>
                        <BdsTableBody>
                            {agentsDataWithTicketsInQueue.map(agent => (
                                <BdsTableRow key={agent.name}>
                                    <BdsTableCell justifyContent="left">{agent.name}</BdsTableCell>
                                    <BdsTableCell type="custom" justifyContent="center">
                                        <BdsTypo variant="fs-14">
                                            <span className={`elipse ${getStatusBagde(agent.status)}`}></span>
                                            {getStatusLabel(agent.status)}
                                        </BdsTypo>
                                    </BdsTableCell>
                                    <BdsTableCell justifyContent="center">{agent.ticketsInQueue}</BdsTableCell>
                                    <BdsTableCell justifyContent="center">{agent.openedTickets}</BdsTableCell>
                                    {!hasMultipleBots && (
                                        <>
                                            <BdsTableCell justifyContent="center">
                                                {agent.averageResponseTime}
                                            </BdsTableCell>
                                            <BdsTableCell justifyContent="center">
                                                {agent.averageAttendanceTime}
                                            </BdsTableCell>
                                        </>
                                    )}
                                </BdsTableRow>
                            ))}
                        </BdsTableBody>
                    </BdsTable>
                    {agentsDataWithTicketsInQueue.length === 0 && (
                        <TableRowEmptyState isSearch={false} messageWhenNoItems={t('go-personal-table-empty-state')} />
                    )}
                </div>
            )}
        </>
    );
};
